// src/interfaces/outlookMailTypes.ts

/**
 * Outlook Mail Tool Type Definitions
 *
 * These types match the backend's outlook_mail tool output.
 * The tool supports three actions: "preview" (show email preview),
 * "send" (send immediately), and "draft" (create in Drafts folder).
 *
 * Backend response structure (via tool_use_result chunk):
 * {
 *   type: "tool_result",
 *   tool_name: "outlook_mail",
 *   tool_use_id: string,
 *   content: { action, status, ... }
 * }
 */

// --- Outer wrapper (what the chunk router receives) ---

export interface OutlookMailChunk {
  type: "tool_result";
  tool_name: "outlook_mail";
  tool_use_id: string;
  content: OutlookMailContentResponse;
}

// --- Inner content types (snake_case, matching BE chunk data) ---

export interface OutlookMailPreviewContent {
  status: "preview";
  action: "preview";
  subject: string;
  body_preview: string;
  to_recipients: string[];
  cc_recipients: string[];
  attach_open_document: boolean;
  attachment_name: string | null;
}

export interface OutlookMailSuccessContent {
  status: "success";
  action: "send" | "draft";
  message_id?: string;
  web_link?: string;
  subject: string;
  to_recipients: string[];
  cc_recipients: string[];
  has_attachment: boolean;
  attachment_name: string | null;
}

export interface OutlookMailErrorContent {
  status: "error";
  action: string;
  message: string;
  error_code: string;
}

export type OutlookMailContentResponse =
  | OutlookMailPreviewContent
  | OutlookMailSuccessContent
  | OutlookMailErrorContent;

// --- Normalized UI type (camelCase) ---

export interface OutlookMailResult {
  status: "preview" | "success" | "error";
  action: "preview" | "send" | "draft";
  toolUseId: string;
  messageId?: string;
  webLink?: string;
  subject: string;
  bodyPreview?: string;
  toRecipients: string[];
  ccRecipients: string[];
  hasAttachment: boolean;
  attachOpenDocument?: boolean;
  attachmentName: string | null;
  errorMessage?: string;
  errorCode?: string;
  source?: "llm" | "direct";
}

// --- Type guard (checks outer wrapper, matches SharePoint pattern) ---

export function isOutlookMailResult(data: unknown): data is OutlookMailChunk {
  if (typeof data !== "object" || data === null) return false;
  const candidate = data as Record<string, unknown>;
  if (candidate.tool_name !== "outlook_mail") return false;
  const content = candidate.content;
  if (typeof content !== "object" || content === null) return false;
  const inner = content as Record<string, unknown>;
  return typeof inner.status === "string" && typeof inner.action === "string";
}

// --- Normalizer (accepts unknown, unwraps outer wrapper) ---

export function normalizeOutlookMailResult(data: unknown): OutlookMailResult {
  const chunk = data as OutlookMailChunk;
  const content = chunk.content;

  if (content.status === "error") {
    return {
      status: "error",
      action: content.action as "preview" | "send" | "draft",
      toolUseId: chunk.tool_use_id,
      subject: "",
      toRecipients: [],
      ccRecipients: [],
      hasAttachment: false,
      attachmentName: null,
      errorMessage: content.message,
      errorCode: content.error_code,
    };
  }

  if (content.status === "preview") {
    return {
      status: "preview",
      action: "preview",
      toolUseId: chunk.tool_use_id,
      subject: content.subject,
      bodyPreview: content.body_preview,
      toRecipients: content.to_recipients,
      ccRecipients: content.cc_recipients,
      hasAttachment: false,
      attachOpenDocument: content.attach_open_document,
      attachmentName: content.attachment_name,
    };
  }

  // status === "success"
  return {
    status: "success",
    action: content.action,
    toolUseId: chunk.tool_use_id,
    messageId: content.message_id,
    webLink: content.web_link,
    subject: content.subject,
    toRecipients: content.to_recipients,
    ccRecipients: content.cc_recipients,
    hasAttachment: content.has_attachment,
    attachOpenDocument: content.has_attachment,
    attachmentName: content.attachment_name,
  };
}

// --- Interactive card types (for direct send/draft flow) ---

export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailData {
  to: EmailRecipient[];
  cc?: EmailRecipient[];
  subject: string;
  body: string;
  attachDocument?: boolean;
  documentName?: string;
}

// --- Direct API response normalizer ---

export function directApiResponseToOutlookMailResult(
  response: {
    action: string;
    subject: string;
    to_recipients: string[];
    cc_recipients: string[];
    has_attachment: boolean;
    attachment_name?: string;
  },
  toolUseId: string,
  bodyPreview?: string
): OutlookMailResult {
  return {
    status: "success",
    action: response.action as "send" | "draft",
    toolUseId,
    subject: response.subject,
    toRecipients: response.to_recipients,
    ccRecipients: response.cc_recipients,
    hasAttachment: response.has_attachment,
    attachOpenDocument: response.has_attachment,
    attachmentName: response.attachment_name || null,
    source: "direct",
    ...(bodyPreview && { bodyPreview }),
  };
}

// --- Tool input parser (for input_json_delta during tool execution) ---

export interface OutlookMailToolInput {
  action?: "preview" | "send" | "draft";
  subject?: string;
  to?: string[];
  attach_open_document?: boolean;
}

export function parseOutlookMailToolInput(
  rawInput: Record<string, unknown>
): OutlookMailToolInput | null {
  if (!rawInput || typeof rawInput !== "object") return null;
  return {
    action: rawInput.action as OutlookMailToolInput["action"],
    subject: rawInput.subject as string | undefined,
    to: rawInput.to as string[] | undefined,
    attach_open_document: rawInput.attach_open_document as boolean | undefined,
  };
}
